import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n } from '../../i18n/index.jsx';
import { Alert, Field } from '../../components/ui.jsx';
import PhoneInput from '../../components/PhoneInput.jsx';
import AuthLayout from '../../components/auth/AuthLayout.jsx';
import { ConsentCheckbox } from './Signup.jsx';
import { safeNext } from './postLogin.js';

const PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

// Members created with "Sign in with Google" land here first: company
// name, phone, location and the privacy-policy consent are required before
// they can post (the server enforces it too).
export default function CompleteProfile() {
  const { t, errorText } = useI18n();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: user.name || '', companyName: user.companyName || '', phone: user.phone || '',
    city: user.city || '', province: user.province || '', consent: user.consentGiven,
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (form.phone.replace(/\D/g, '').length !== 10) return setError(t('errors.invalid_phone'));
    if (!form.consent) return setError(t('errors.consent_required'));
    setBusy(true);
    try {
      setUser(await api('/auth/complete-profile', { method: 'POST', body: form }));
      navigate(safeNext(params.get('next')) || '/account', { replace: true });
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <AuthLayout titleKey="completeProfile.title" title={t('completeProfile.title')} lead={t('completeProfile.lead', { email: user.email })}>
      <form onSubmit={submit} className="form" noValidate>
        <div className="grid-form">
          <Field label={t('auth.name')} htmlFor="cp-name">
            <input id="cp-name" value={form.name} onChange={set('name')} autoComplete="name" required maxLength={120} />
          </Field>
          <Field label={t('auth.companyName')} htmlFor="cp-company">
            <input id="cp-company" value={form.companyName} onChange={set('companyName')} autoComplete="organization" required maxLength={160} />
          </Field>
        </div>
        <Field label={t('auth.phone')} hint={t('profile.phoneHint')} htmlFor="cp-phone">
          <PhoneInput id="cp-phone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} required />
        </Field>
        <div className="grid-form">
          <Field label={t('profile.city')} optional htmlFor="cp-city"><input id="cp-city" value={form.city} onChange={set('city')} maxLength={120} /></Field>
          <Field label={t('profile.province')} optional htmlFor="cp-prov">
            <select id="cp-prov" value={form.province} onChange={set('province')}>
              <option value="">{t('profile.choose')}</option>
              {PROVINCES.map((p) => <option key={p} value={p}>{t(`provinces.${p}`)}</option>)}
            </select>
          </Field>
        </div>
        <ConsentCheckbox checked={form.consent} onChange={(v) => setForm((f) => ({ ...f, consent: v }))} />
        <Alert>{error}</Alert>
        <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={busy}>
          {busy ? t('common.saving') : t('completeProfile.submit')}
        </button>
      </form>
    </AuthLayout>
  );
}
